/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { ESocketEvents } from '@lbs/shared-rp-types'
import { rpMediaQueries } from 'core/theme'
import { useSelector } from '@xstate/react'
import { observer } from 'mobx-react-lite'
import { useRpSocketContext } from 'providers/RpSocketProvider'
import { useRpService, useRussianPokerStore } from 'providers/RussianPokerProvider'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { rpSelectors } from 'machines/RussianPoker'
import { BettingBox, BtnYellow, Typography } from 'rpComponents'
import BoxDealer from 'rpComponents/BoxDealer'
import DecisionBox from 'rpComponents/DecisionBox'
import { ReloadIcon } from 'rpComponents/Icons'
import { RoundIconBtn } from 'rpComponents/RoundIconBtn'

const Container = styled.div`
  height: 100%;
  width: 100%;
  max-height: 100%;
  z-index: 2;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;

  // ${rpMediaQueries.tablet} {
  //   padding: 8px;
  //   gap: 12px;

  //   @supports (top: env(safe-area-inset-top)) {
  //     margin-top: calc(8px + env(safe-area-inset-top));
  //     margin-right: calc(8px + env(safe-area-inset-right));
  //   }
  // }

  ${rpMediaQueries.desktop} {
    gap: 16px;
  }
`

const GameButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: self-end;
  flex-direction: column;
`

const GameButtonsContainerInside = styled.div`
  min-width: 100%;
  gap: 12px;
  border-radius: 5px;
  border: 0.5px solid rgba(255, 255, 255, 0.15);

  display: flex;
  align-items: self-end;
  justify-content: end;

  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) -9.58%,
      rgba(255, 255, 255, 0.05) 100%
    ),
    linear-gradient(0deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0.25) 100%)
      no-repeat,
    #000;

  box-shadow: 0 20px 50px 0 rgba(255, 255, 255, 0.12) inset,
    0 1px 0 0 rgba(0, 0, 0, 0.05), 0 4px 4px 0 rgba(0, 0, 0, 0.05),
    0 10px 10px 0 rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(1px);
  padding: 4px;

  ${rpMediaQueries.tablet} {
    padding: 8px;
    gap: 16px;
  }
`

const CommonPanel = styled.div`
  align-items: self-end;
  flex-direction: column;
  display: flex;
  gap: 4px;

  ${rpMediaQueries.tablet} {
    gap: 8px;
  }
`

export const SidePanel: FC<{
  className?: string
}> = observer((props) => {
  const { className } = props
  const { t } = useTranslation()
  const { isReadyToGame, isFinishPhase, game, boxes } = useRussianPokerStore()
  const { emit } = useRpSocketContext()
  const rpService = useRpService()

  const isBettingPhase = useSelector(rpService, rpSelectors.isBettingPhase)

  const activeBoxes = boxes.filter(
    (box) => box.hasStake || box.result.outcome !== 'none'
  )

  const renderBettingBoxes = () =>
    boxes.map((box) => <BettingBox key={box.seatNumber} box={box} />)

  const renderDecisionBoxes = () => (
    <>
      <BoxDealer />
      {activeBoxes.map((box) => (
        <DecisionBox box={box} key={box.seatNumber} />
      ))}
    </>
  )

  return (
    <Container className={className}>
      <CommonPanel>
        {isBettingPhase ? renderBettingBoxes() : renderDecisionBoxes()}
      </CommonPanel>

      <GameButtonsContainer>
        {isReadyToGame && (
          <GameButtonsContainerInside>
            <BtnYellow
              data-cy="play-game-btn"
              onClick={() => {
                emit(ESocketEvents.READY_TO_PLAY, {
                  gameId: game.gameId,
                  sessionId: game.sessionId,
                })
              }}
              fullWidth>
              <Typography variant="yellowBtn" color="black" as="span">
                {t('playGame')}
              </Typography>
            </BtnYellow>
          </GameButtonsContainerInside>
        )}

        {isFinishPhase && (
          <GameButtonsContainerInside>
            <RoundIconBtn
              data-cy="repeat-bets-btn"
              onClick={() => {
                emit(ESocketEvents.REPEAT_BETS, game.gameId)
              }}
              icon={
                <ReloadIcon
                  size={{
                    mobile: '8px',
                    tablet: '15px',
                    desktop: '16px',
                  }}
                />
              }
            />
            <BtnYellow
              data-cy="play-again-btn"
              onClick={() => {
                emit(ESocketEvents.PLAY_AGAIN, game.gameId)
              }}
              fullWidth>
              <Typography variant="yellowBtn" color="black" as="span">
                {t('playAgain')}
              </Typography>
            </BtnYellow>
          </GameButtonsContainerInside>
        )}
      </GameButtonsContainer>
    </Container>
  )
})
